import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import {
  filterBonuses,
  type BonusBucket,
  type BonusModuleFilter,
  type BonusTransaction,
  type BonusTransactionDetails,
  type BonusTransactionDetailsRequest,
  type BonusTransactionsListRequest,
} from '@/domain/bonuses';
import type {
  LostBonusClaimRequest,
  LostBonusClaimResponse,
  LostBonusOffer,
  LostBonusesListRequest,
} from '@/domain/lost-bonuses';
import { applyDepositBonusSettingsMessage } from '@/player/apply-deposit-bonus-settings';
import { getUserSettings, updateUserCredits } from './auth';
import { api } from './client';
import { endpoints } from './endpoints';
import { queryKeys } from './query-keys';

/** Profile bonus list (`sb/api/player/profile/bonustransed`). */
export function listBonusTransactions(
  request: BonusTransactionsListRequest,
  signal?: AbortSignal,
): Promise<BonusTransaction[]> {
  return api.post<BonusTransaction[]>(
    endpoints.player.bonusTransactions.host,
    endpoints.player.bonusTransactions.path,
    request as unknown as Record<string, unknown>,
    signal,
  );
}

/** Profile bonus details (`sb/api/player/profile/bonustranseddetails`). */
export function getBonusTransactionDetails(
  request: BonusTransactionDetailsRequest,
  signal?: AbortSignal,
): Promise<BonusTransactionDetails> {
  return api.post<BonusTransactionDetails>(
    endpoints.player.bonusTransactionDetails.host,
    endpoints.player.bonusTransactionDetails.path,
    request as unknown as Record<string, unknown>,
    signal,
  );
}

export interface UseBonusesResult {
  bucket: BonusBucket;
  setBucket: (bucket: BonusBucket) => void;
  module: BonusModuleFilter;
  setModule: (module: BonusModuleFilter) => void;
  rows: BonusTransaction[];
  activeCount: number;
  historyCount: number;
  isLoading: boolean;
  listError: Error | null;
  currency: string;
}

/**
 * Bonuses list: fetch once, filter Active/History + All/Sport/Casino client-side
 * (legacy BonusCounter / BonusItem rules).
 */
export function useBonuses(enabled = true): UseBonusesResult {
  const [bucket, setBucket] = useState<BonusBucket>('active');
  const [module, setModule] = useState<BonusModuleFilter>('all');

  const user = getUserSettings();
  const machineGuid = user?.Guid?.trim() ?? '';
  const currency = (user?.Currency ?? user?.CurrCode ?? user?.CurrencyCode ?? '').trim();

  const listQuery = useQuery({
    queryKey: queryKeys.player.bonuses({ machineGuid }),
    enabled: enabled && Boolean(machineGuid),
    queryFn: async ({ signal }) => {
      const response = await listBonusTransactions({ Value: machineGuid }, signal);
      return Array.isArray(response) ? response : [];
    },
  });

  const allRows = listQuery.data ?? [];

  const activeCount = useMemo(
    () => filterBonuses(allRows, 'active', 'all').length,
    [allRows],
  );
  const historyCount = useMemo(
    () => filterBonuses(allRows, 'history', 'all').length,
    [allRows],
  );

  const rows = useMemo(
    () => filterBonuses(allRows, bucket, module),
    [allRows, bucket, module],
  );

  return {
    bucket,
    setBucket,
    module,
    setModule,
    rows,
    activeCount,
    historyCount,
    isLoading: listQuery.isLoading,
    listError: listQuery.error instanceof Error ? listQuery.error : null,
    currency,
  };
}

export interface UseBonusDetailsResult {
  details: BonusTransactionDetails | null;
  isLoading: boolean;
  error: Error | null;
  currency: string;
}

export function useBonusDetails(
  bonusType: number | null,
  bonusId: string | null,
  enabled = true,
): UseBonusDetailsResult {
  const user = getUserSettings();
  const currency = (user?.Currency ?? user?.CurrCode ?? user?.CurrencyCode ?? '').trim();

  const detailsQuery = useQuery({
    queryKey: queryKeys.player.bonusDetail(bonusType, bonusId),
    enabled: enabled && bonusType != null && Boolean(bonusId),
    queryFn: async ({ signal }) => {
      return getBonusTransactionDetails(
        { Value: bonusId!, Type: bonusType! },
        signal,
      );
    },
  });

  return {
    details: detailsQuery.data ?? null,
    isLoading: detailsQuery.isLoading,
    error: detailsQuery.error instanceof Error ? detailsQuery.error : null,
    currency,
  };
}

/** Claimable lost-bonus offers (`sb/api/player/profile/lostbonuses`). */
export function listLostBonuses(
  request: LostBonusesListRequest,
  signal?: AbortSignal,
): Promise<LostBonusOffer[]> {
  return api.post<LostBonusOffer[]>(
    endpoints.player.lostBonuses.host,
    endpoints.player.lostBonuses.path,
    request as unknown as Record<string, unknown>,
    signal,
  );
}

/** Confirm a lost-bonus claim (`sb/api/player/profile/lostbonuscreate`). */
export function confirmLostBonus(
  request: LostBonusClaimRequest,
  signal?: AbortSignal,
): Promise<LostBonusClaimResponse> {
  return api.post<LostBonusClaimResponse>(
    endpoints.player.lostBonusConfirm.host,
    endpoints.player.lostBonusConfirm.path,
    request as unknown as Record<string, unknown>,
    signal,
  );
}

export interface UseLostBonusesResult {
  offers: LostBonusOffer[];
  isLoading: boolean;
  listError: Error | null;
  refetch: () => void;
}

export function useLostBonuses(enabled = true): UseLostBonusesResult {
  const user = getUserSettings();
  const machineGuid = user?.Guid?.trim() ?? '';

  const listQuery = useQuery({
    queryKey: queryKeys.player.lostBonuses({ machineGuid }),
    enabled: enabled && Boolean(machineGuid),
    queryFn: async ({ signal }) => {
      const response = await listLostBonuses({ MachineGuid: machineGuid }, signal);
      return Array.isArray(response) ? response : [];
    },
  });

  return {
    offers: listQuery.data ?? [],
    isLoading: listQuery.isLoading,
    listError: listQuery.error instanceof Error ? listQuery.error : null,
    refetch: () => {
      void listQuery.refetch();
    },
  };
}

export interface UseConfirmLostBonusResult {
  confirm: (accountBonusId: string) => Promise<LostBonusClaimResponse>;
  isConfirming: boolean;
  error: Error | null;
  result: LostBonusClaimResponse | null;
  reset: () => void;
}

export function useConfirmLostBonus(): UseConfirmLostBonusResult {
  const queryClient = useQueryClient();
  const machineGuid = getUserSettings()?.Guid?.trim() ?? '';

  const mutation = useMutation({
    mutationFn: (accountBonusId: string) =>
      confirmLostBonus({ MachineGuid: machineGuid, AccountBonusId: accountBonusId }),
    onSuccess: async (response) => {
      if (response?.Success && response.CreditsData) {
        updateUserCredits(response.CreditsData);
      }
      if (response?.Success && response.DepositBonusSettingsMessage) {
        applyDepositBonusSettingsMessage(response.DepositBonusSettingsMessage);
      }
      if (response?.Success) {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: queryKeys.player.lostBonuses({ machineGuid }),
          }),
          queryClient.invalidateQueries({
            queryKey: queryKeys.player.bonuses({ machineGuid }),
          }),
        ]);
      }
    },
  });

  return {
    confirm: (accountBonusId) => mutation.mutateAsync(accountBonusId),
    isConfirming: mutation.isPending,
    error: mutation.error instanceof Error ? mutation.error : null,
    result: mutation.data ?? null,
    reset: () => mutation.reset(),
  };
}
